/**
 * Re-used from the landing-page spec — same scroll-reveal class machinery.
 *
 * Adding `.reveal*` to a card gives it a blur-to-clear fade-in on viewport entry.
 */
export function useScrollRevealAll(selector: string, threshold = 0.15) {
  if (typeof window === "undefined") return;

  const elements = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
  if (elements.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add("visible");
      });
    },
    { threshold },
  );

  elements.forEach((el) => observer.observe(el));
  return () => observer.disconnect();
}

/**
 * Animates 0 → to on first viewport entry. Uses rAF to drive a MotionValue-like
 * state via React re-renders. (We don't pull framer-motion here — keeping the
 * dependency footprint tight for the admin.)
 */
import { useEffect, useRef, useState } from "react";

export function useCounter(target: number, duration = 1500) {
  const ref = useRef<HTMLSpanElement>(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    let cancelled = false;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !cancelled) {
            const start = performance.now();
            const tick = (t: number) => {
              if (cancelled) return;
              const p = Math.min(1, (t - start) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              setValue(Math.round(eased * target));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 },
    );
    io.observe(ref.current);
    return () => {
      cancelled = true;
      io.disconnect();
    };
  }, [target, duration]);

  return { ref, value };
}
